import { global } from "../env/global";
import { STR_MODULE } from "../env/strings";

import {
  DynamicImport,
  parseDynamicImport,
} from "../shared-utils/vite.preload";

import { runBootPreloads } from "../shared-utils/vite.bootloader";

import { WorkerUrl } from "./urls";
import { captureMessage } from "@sentry/core";

// this function will be eliminated in normal builds
// see: https://github.com/vitejs/vite/issues/10886
function perfWarn(state: string): void {
  if (!import.meta.env.DEBUG_LOAD_TIME) return;
  console.warn(`[${performance.now()}] PDF ${state} from ${import.meta.url}`);
}

const mark = (state: string) => (
  perfWarn(state),
  performance.mark(
    `${import.meta.env.BOOTLOADER ? "boot" : "main"}-pdf-${state}`
  )
);

declare global {
  interface CustomGlobalConstants {
    _DM_PDF_Wrk?: Worker | "claimed";
  }
}

type PDFjsPreloadData = {
  worker: {
    url: string;
    claim(): Worker;
  };
  library: DynamicImport<typeof import("pdfjs-dist/build/pdf")>;
  viewer: DynamicImport<typeof import("pdfjs-dist/web/pdf_viewer.mjs")>;
  navigator: DynamicImport<
    typeof import("../../src/components/navigator/PDFjsNavigator.vue")
  >;
};

function handoffFromBootloader(): PDFjsPreloadData {
  const isPreload =
    new URL(location.href).searchParams.get("preload") === "pdf";
  let worker: Worker | null = null;
  if (global._DM_PDF_Wrk instanceof Worker) {
    mark("handoff");
    worker = global._DM_PDF_Wrk;
    if (!isPreload) {
      captureMessage("PDF worker instance handed off outside of preload", {
        level: "warning",
      });
    }
  } else if (isPreload) {
    mark("start");
    worker = new Worker(WorkerUrl, {
      type: STR_MODULE,
    });
  }

  /**
   * If the main app bundle was to execute before the pdf bootloader,
   * this would ensure the bootloader doesn't create another instance
   */
  global._DM_PDF_Wrk = "claimed";

  return {
    worker: {
      url: WorkerUrl,
      claim() {
        try {
          mark("claim");
          if (worker) return worker;

          mark("start");
          return new Worker(WorkerUrl, {
            type: STR_MODULE,
          });
        } finally {
          worker = null;
        }
      },
    },
    library: parseDynamicImport(
      () => import("pdfjs-dist/build/pdf"),
      import.meta
    ),
    viewer: parseDynamicImport(
      () => import("pdfjs-dist/web/pdf_viewer.mjs"),
      import.meta
    ),
    navigator: parseDynamicImport(
      () => import("../../src/components/navigator/PDFjsNavigator.vue"),
      import.meta
    ),
  };
}

export const PDFjsPreload = import.meta.env.BOOTLOADER
  ? ((global._DM_PDF_Wrk ||=
      (mark("start"),
      runBootPreloads(
        () => import("pdfjs-dist/build/pdf"),
        () => import("pdfjs-dist/web/pdf_viewer.mjs"),
        () => import("../../src/components/navigator/PDFjsNavigator.vue")
      ),
      new Worker(WorkerUrl, {
        type: STR_MODULE,
      }))) as unknown as PDFjsPreloadData)
  : handoffFromBootloader();
